import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { fabric } from 'fabric'
import { v4 as uuid } from 'uuid'
import {
    StoredIndividualization,
    StoredSpot,
    StoredTemplate,
} from '../types/Individualization'
import { Font } from '../types/Font'
import { SubscriptionData } from '../types/Subscription'
import { useFetchData } from '../hooks/useFetchData'
import { useFetchSingle } from '../hooks/useFetchSingle'
import { ButtonSetting } from '../types/ButtonSetting'
import { Article } from '../types/Article'
import { getTranslatedObject } from '../helpers/getTranslatedObject'
import { ApiRoutes } from '../services/ApiService'
import { loadClipPath } from '../helpers/loadClipPath'
import { Canvas } from 'fabric/fabric-impl'
import { usePluginContext } from './PluginContext'
import { SettingType } from '../types/GlobalSetting'

type CustomizerContextValues = {
    article: Article | null
    templates: StoredTemplate[]
    individualization: StoredIndividualization
    userInputs: Map<string, fabric.Object>
    setUserInputs: React.Dispatch<
        React.SetStateAction<Map<string, fabric.Object>>
    >
    activeTemplate: StoredTemplate | undefined
    activeTemplateId: number | null
    setActiveTemplateId: React.Dispatch<React.SetStateAction<number | null>>
    getClipPath: (canvas: Canvas, spot_id: number) => fabric.Object | undefined
    getFabricByUniqueId: (unique_id: string) => fabric.Object | null
    fonts: Font[]
    buttonSetting: ButtonSetting | null
    uuids: Map<number, string>
    cuttingMaskElements: Map<number, { uid: string; type: 'text' | 'image' }[]>
    setCuttingMaskElements: React.Dispatch<
        React.SetStateAction<
            Map<number, { uid: string; type: 'text' | 'image' }[]>
        >
    >
    downloadCustomization: () => void
    generateThumbnails: () => {
        template_id: number
        thumbnail: string
    }[]
    kArtikel: number | undefined
    isLoading: boolean
    subscription: SubscriptionData | null
}

const CustomizerContext = createContext<CustomizerContextValues | undefined>(
    undefined
)

export const CustomizerContextProvider: React.FC<{
    children: React.ReactNode
    individualization: StoredIndividualization
    kArtikel: number | undefined
}> = ({ children, individualization, kArtikel }) => {
    const [userInputs, setUserInputs] = useState<Map<string, fabric.Object>>(
        new Map()
    )
    const [clipPathsLoadedToTemplate, setClipPathsLoadedToTemplate] = useState<number[]>(
        []
    )
    const [activeTemplateId, setActiveTemplateId] = useState<number | null>(
        null
    )
    const [cuttingMaskElements, setCuttingMaskElements] = useState<
        Map<number, { uid: string; type: 'text' | 'image' }[]>
    >(new Map())
    const { plugin , getSettingValue, globalSetting, isGlobalSettingLoading } = usePluginContext()
    const { data: fonts, fetch: fetchFonts } = useFetchData<Font>(ApiRoutes.FONTS)

    const {
        data: buttonSetting,
        fetch: fetchButton,
        isLoading: isButtonLoading,
    } = useFetchSingle<ButtonSetting>(ApiRoutes.BUTTON_SETTINGS)

    const {
        data: article,
        fetch: fetchArticle,
        isLoading: isArticleLoading,
    } = useFetchSingle<Article>(ApiRoutes.ARTICLES)

    const {
        data: subscription,
        fetch: fetchSubscription,
        isLoading: isSubscriptionLoading,
    } = useFetchSingle<SubscriptionData>(ApiRoutes.UPDATE_SUBSCRIPTION)

    useEffect(() => {
        fetchSubscription()
    }, [fetchSubscription])

    useEffect(() => {
        fetchButton()
    }, [fetchButton])

    useEffect(() => {
        fetchArticle(kArtikel)
    }, [kArtikel, fetchArticle])

    const templates: StoredTemplate[] = useMemo(() => {
        return (
            individualization.templates.map((template) => {
                template.canvas = new fabric.Canvas(
                    document.createElement('canvas'),
                    {
                        backgroundColor: 'white',
                        selection: false,
                        renderOnAddRemove: true,
                        preserveObjectStacking: true,
                    }
                )
                return template
            }) || []
        )
    }, [individualization.templates])

    const isLoading = useMemo(() => {
        return isSubscriptionLoading || isButtonLoading || isArticleLoading || isGlobalSettingLoading
    }, [isSubscriptionLoading, isButtonLoading, isArticleLoading, isGlobalSettingLoading])

    const getClipPath = useCallback((c: Canvas, spot_id: number) => {
        return c.getObjects().find((obj) => {
            return obj.data?.spot_id === spot_id && obj.data?.is_clip_path
        })
    }, [])

    const activeTemplate: StoredTemplate | undefined = useMemo(() => {
        if (subscription === null) {
            return undefined
        }

        const template = templates.find(
            (template) => template.id === activeTemplateId
        )

        const hasPlan =
            subscription.plans.filter(
                (p) => p.slug === 'zr-customizer-freier-modus'
            ).length > 0

        if (template && !hasPlan) {
            template.spots = template.spots.filter((s: StoredSpot) => {
                return s.type !== 'cutting mask'
            })
        }

        return template
    }, [activeTemplateId, templates, subscription])

    const uuids = useMemo(() => {
        const unique_ids = new Map<number, string>()
        individualization.templates.forEach((t) => {
            t.spots.forEach((s) => {
                if (['text', 'image', 'auto text'].includes(s.type)) {
                    unique_ids.set(s.id, uuid())
                }
            })
        })
        return unique_ids
    }, [individualization])

    const addClipPath = useCallback(
        (c: Canvas, spot: StoredSpot) => {
            const color = getSettingValue('border_color_in_canvas', SettingType.COLOR || '#525252')
            const showBorders = getSettingValue('show_border_in_canvas', SettingType.BOOLEAN)
            const showCaption = getSettingValue('show_caption_in_canvas', SettingType.BOOLEAN)
            const element = loadClipPath(spot, color, showBorders ? 2 : 0)

            c.add(element)

            const translatedSpot = getTranslatedObject(spot, plugin?.language)
            if (showCaption) {
                c.add(
                    new fabric.Text(translatedSpot.name, {
                        fontSize: 12,
                        left: element.getBoundingRect(true).left,
                        top: element.getBoundingRect(true).top - 20,
                        fill: color,
                        hoverCursor: 'default',
                        selectable: false,
                    })
                )
            }
        },
        [plugin, getSettingValue]
    )

    const loadTemplateUserInputs = useCallback(
        (template: StoredTemplate) => {
            [...userInputs.values()].forEach((obj) => {
                if (obj.data?.template_id === template.id && template.canvas) {
                    template.canvas.add(obj)
                }
            })
        },
        [userInputs]
    )

    const fillCanvas = (template: StoredTemplate) => {
        if (!clipPathsLoadedToTemplate.includes(template.id)){
            template.spots.forEach((spot) => {
                if (!template.canvas) return
                addClipPath(template.canvas, spot)
            })
            setClipPathsLoadedToTemplate([...clipPathsLoadedToTemplate, template.id])
        }
    }

    const getFabricByUniqueId = useCallback(
        (unique_id: string) => {
            const ele = userInputs.get(unique_id)
            return ele ?? null
        },
        [userInputs]
    )

    const downloadCustomization = () => {
        for (const page of generateThumbnails()) {
            const pseudoLink: HTMLAnchorElement = document.createElement('a')

            pseudoLink.href = page.thumbnail
            pseudoLink.download =
                'canvas_' +
                page.template_id +
                '_' +
                Date.now().toString() +
                '.png'
            pseudoLink.click()

            pseudoLink.remove()
        }
    }

    const generateThumbnails = useCallback(() => {
        const files: {
            template_id: number
            thumbnail: string
        }[] = []

        for (const t of templates) {
            if (!t.canvas) continue

            const thumbnail = (t.canvas as fabric.Canvas).toDataURL({
                format: 'png',
                quality: 1,
                multiplier: 300 / (t.canvas as fabric.Canvas).getWidth(),
            })

            files.push({
                template_id: t.id,
                thumbnail,
            })
        }

        return files
    }, [templates])

    useEffect(() => {
        if (individualization.templates.length > 0) {
            setActiveTemplateId(individualization.templates[0].id)
        }
    }, [individualization])

    useEffect(() => {
        if (activeTemplate && globalSetting && globalSetting.length) fillCanvas(activeTemplate)
    }, [activeTemplate, globalSetting])

    useEffect(() => {
        fetchFonts({'orderBy': 'name'})
    }, [fetchFonts])

    return (
        <CustomizerContext.Provider
            value={{
                kArtikel,
                article,
                isLoading,
                individualization,
                userInputs,
                setUserInputs,
                activeTemplate,
                templates,
                activeTemplateId,
                setActiveTemplateId,
                getClipPath,
                getFabricByUniqueId,
                fonts,
                buttonSetting,
                uuids,
                cuttingMaskElements,
                setCuttingMaskElements,
                downloadCustomization,
                generateThumbnails,
                subscription,
            }}
        >
            {children}
        </CustomizerContext.Provider>
    )
}

export const useCustomizer = () => {
    const data = useContext(CustomizerContext)

    if (data === undefined) {
        throw new Error(
            'useCustomizer must used within CustomizerContextProvider'
        )
    }

    return data
}
